import { useEffect, useState, useRef, useMemo } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { Navbar } from '@/components/layout/Navbar';
import { FilterBar } from '@/components/filters/FilterBar';
import {
  productApi,
  subProductApi,
  initiativeApi,
  classificationApi,
  statusApi,
  tagApi,
  userApi,
  fiscalYearApi,
  fieldConfigApi,
  labsApi,
} from '@/services/api';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { INITIATIVE_TITLE_LINK_HOVER_CLASS } from '@/utils/uiConventions';
import { ClassificationFooter } from '@/components/ui/ClassificationFooter';

interface Initiative {
  id: string;
  title: string;
  subProductId: string;
  subProduct?: {
    id: string;
    name: string;
    productId: string;
    product?: {
      id: string;
      name: string;
    };
  };
  status?: {
    id: string;
    name: string;
    color: string;
  };
  classification?: {
    id: string;
    name: string;
    color: string;
  };
  user?: {
    id: string;
    name: string;
  };
  startQuarter: string;
  endQuarter?: string;
  dependencies?: Array<{
    id: string;
    dependsOnInitiativeId: string;
    dependsOnInitiative?: Initiative;
  }>;
}

export function DependencyLogPage() {
  const {
    quarters,
    setProducts,
    setInitiatives,
    setClassifications,
    setStatuses,
    setTags,
    setOwners,
    setQuarters,
    setFieldConfigs,
    setLabsFeatures,
    setLoading,
    setError,
    refreshTrigger,
    openViewInitiativeModal,
  } = useAppStore();
  const fc = useFieldConfig();

  const [filteredInitiatives, setFilteredInitiatives] = useState<Initiative[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const viewContainerRef = useRef<HTMLDivElement>(null);

  // Get only initiatives with dependencies
  const initiativesWithDependencies = filteredInitiatives.filter(
    (init) => init.dependencies && init.dependencies.length > 0
  );

  // Calculate counts for display
  const { parentCount, dependencyCount, visibleRowCount } = useMemo(() => {
    let depCount = 0;
    initiativesWithDependencies.forEach((init) => {
      depCount += init.dependencies?.length || 0;
    });
    return {
      parentCount: initiativesWithDependencies.length,
      dependencyCount: depCount,
      visibleRowCount: initiativesWithDependencies.length + depCount,
    };
  }, [initiativesWithDependencies]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        productsData,
        subProductsData,
        initiativesData,
        classificationsData,
        statusesData,
        tagsData,
        usersData,
        fiscalYearsData,
        fieldConfigData,
        labsData,
      ] = await Promise.all([
        productApi.getAll(),
        subProductApi.getAll(),
        initiativeApi.getAll(),
        classificationApi.getAll(),
        statusApi.getAll(),
        tagApi.getAll(),
        userApi.getAll(),
        fiscalYearApi.getAll(),
        fieldConfigApi.getAll().catch(() => []),
        labsApi.getAll().catch(() => []),
      ]);

      const productsWithSubProducts = productsData.map((product: any) => ({
        ...product,
        subProducts: subProductsData.filter(
          (sp: any) => sp.productId === product.id
        ),
      }));

      setProducts(productsWithSubProducts);
      setInitiatives(initiativesData);
      setClassifications(classificationsData);
      setStatuses(statusesData);
      setTags(tagsData);
      setOwners(usersData);
      if (fieldConfigData.length > 0) {
        setFieldConfigs(fieldConfigData);
      }
      if (labsData.length > 0) {
        setLabsFeatures(labsData);
      }

      const allQuarters = fiscalYearsData
        .flatMap((fy: any) => fy.quarters || [])
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

      setQuarters(allQuarters);

      // Expand all rows by default
      const allIds = new Set(
        initiativesData
          .filter((init: any) => init.dependencies && init.dependencies.length > 0)
          .map((init: any) => init.id)
      );
      setExpandedRows(allIds);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load dependency data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatQuarterRange = (start: string, end?: string) => {
    const startQ = quarters.find((q) => q.key === start);
    const endQ = end ? quarters.find((q) => q.key === end) : null;

    if (!startQ) return start;
    if (!endQ || start === end) return startQ.fullLabel || start;
    return `${startQ.fullLabel} → ${endQ.fullLabel}`;
  };

  const getProductName = (init: Initiative) => {
    if (init.subProduct?.product?.name && init.subProduct?.name) {
      return `${init.subProduct.product.name} > ${init.subProduct.name}`;
    }
    return init.subProduct?.name || '-';
  };

  return (
    <div className="h-screen flex flex-col bg-background-primary">
      <Navbar />

      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-3 sm:px-6 py-3 bg-background-primary">
            <FilterBar
              onFilteredInitiativesChange={setFilteredInitiatives}
              viewType="list"
              viewContainerRef={viewContainerRef}
              customCount={visibleRowCount}
              customCountLabel="Rows"
              hideHasDependencies
            />
          </div>

          <div className="flex-1 overflow-hidden px-6 pb-6" ref={viewContainerRef}>
            <div className="h-full overflow-auto">
              {initiativesWithDependencies.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-text-secondary">
                  No initiatives with dependencies found
                </div>
              ) : (
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10 bg-background-secondary">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary w-8"></th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">
                        <span className="inline-flex items-center gap-1.5">
                          {fc.label('initiative')}
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-normal">{parentCount}</span>
                          <span className="text-text-secondary font-normal">/</span>
                          {fc.label('dependencies')}
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-normal">{dependencyCount}</span>
                        </span>
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary whitespace-nowrap">
                        {fc.label('product')} / {fc.label('subProduct')}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary whitespace-nowrap">
                        {fc.label('status')}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary whitespace-nowrap">
                        Timeline
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary whitespace-nowrap">
                        {fc.label('owner')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {initiativesWithDependencies.map((initiative) => {
                      const isExpanded = expandedRows.has(initiative.id);
                      const depCount = initiative.dependencies?.length || 0;

                      return (
                        <>
                          {/* Parent Initiative Row */}
                          <tr
                            key={initiative.id}
                            className="border-b border-gray-100 bg-blue-50/50 hover:bg-blue-50 cursor-pointer"
                            onClick={() => toggleRow(initiative.id)}
                          >
                            <td className="py-3 px-4">
                              <button className="p-1 hover:bg-blue-100 rounded">
                                {isExpanded ? (
                                  <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                                ) : (
                                  <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-1 h-6 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: fc.isVisible('classification') ? (initiative.classification?.color || '#6B7280') : '#D1D5DB' }}
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openViewInitiativeModal(initiative as any);
                                  }}
                                  className={`font-medium text-text-primary ${INITIATIVE_TITLE_LINK_HOVER_CLASS} text-left`}
                                >
                                  {initiative.title}
                                </button>
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                  {depCount}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-text-secondary max-w-[220px] truncate" title={getProductName(initiative)}>
                              {getProductName(initiative)}
                            </td>
                            <td className="py-3 px-4">
                              {initiative.status && (
                                <span
                                  className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full"
                                  style={{
                                    backgroundColor: `${initiative.status.color}20`,
                                    color: initiative.status.color,
                                  }}
                                >
                                  <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: initiative.status.color }}
                                  />
                                  {initiative.status.name}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-xs text-text-secondary whitespace-nowrap">
                              {formatQuarterRange(initiative.startQuarter, initiative.endQuarter)}
                            </td>
                            <td className="py-3 px-4 text-xs text-text-secondary whitespace-nowrap">
                              {initiative.user?.name || '-'}
                            </td>
                          </tr>

                          {/* Dependency Rows */}
                          {isExpanded &&
                            initiative.dependencies?.map((dep) => {
                              const depInit = dep.dependsOnInitiative;
                              if (!depInit) return null;

                              return (
                                <tr
                                  key={dep.id}
                                  className="border-b border-gray-100 hover:bg-surface-hover"
                                >
                                  <td className="py-2 px-4"></td>
                                  <td className="py-2 px-4">
                                    <div className="flex items-center gap-2 pl-4">
                                      <span className="text-gray-400">↳</span>
                                      <div
                                        className="w-1 h-5 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: fc.isVisible('classification') ? (depInit.classification?.color || '#6B7280') : '#D1D5DB' }}
                                      />
                                      <button
                                        onClick={() => openViewInitiativeModal(depInit as any)}
                                        className={`text-sm text-text-secondary ${INITIATIVE_TITLE_LINK_HOVER_CLASS} text-left`}
                                      >
                                        {depInit.title}
                                      </button>
                                    </div>
                                  </td>
                                  <td className="py-2 px-4 text-sm text-text-tertiary max-w-[220px] truncate" title={getProductName(depInit)}>
                                    {getProductName(depInit)}
                                  </td>
                                  <td className="py-2 px-4">
                                    {depInit.status && (
                                      <span
                                        className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full"
                                        style={{
                                          backgroundColor: `${depInit.status.color}20`,
                                          color: depInit.status.color,
                                        }}
                                      >
                                        <span
                                          className="w-1.5 h-1.5 rounded-full"
                                          style={{ backgroundColor: depInit.status.color }}
                                        />
                                        {depInit.status.name}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-2 px-4 text-xs text-text-tertiary whitespace-nowrap">
                                    {formatQuarterRange(depInit.startQuarter, depInit.endQuarter)}
                                  </td>
                                  <td className="py-2 px-4 text-xs text-text-tertiary whitespace-nowrap">
                                    {depInit.user?.name || '-'}
                                  </td>
                                </tr>
                              );
                            })}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <ClassificationFooter />
        </div>
      </main>
    </div>
  );
}
